import { BulkTemplateResult, BulkTemplateRow } from '../../api/admin-api';

export type AppetiteSetupView = 'classic' | 'universal';

export const BULK_ROW_LIMIT = 4000;

export type { BulkTemplateResult, BulkTemplateRow };

export interface ParsedBulkRow {
    assocId: number;
    usStateId: number;
    stateCode: string;
    keyword: string;
    businessClass: string;
    weight: number;
}

export interface ParsedBulkUpload {
    rows: ParsedBulkRow[];
    errors: string[];
    warnings: string[];
    activeCount: number;
    inactiveCount: number;
}

const CLASSIC_HEADERS = [
    'Carrier', 'CarrierId', 'LineOfBusiness', 'LineOfBusinessId', 'Keyword', 'KeywordId',
    'LobKeywordAssocId', 'State', 'UsStateId', 'BusinessClass', 'Active',
];

const UNIVERSAL_HEADERS = [
    'Carrier', 'CarrierId', 'LineOfBusiness', 'LineOfBusinessId', 'PrimaryKeyword', 'SecondaryKeyword',
    'UniversalKeyPairId', 'LobUniversalKeywordAssocId', 'State', 'UsStateId', 'BusinessClass', 'Active',
];

export function templateHeaders(view: string) {
    return view === 'universal' ? UNIVERSAL_HEADERS : CLASSIC_HEADERS;
}

export function businessClassLimit(view: string) {
    return view === 'universal' ? 500 : 200;
}

function csvCell(value: string | number | null | undefined) {
    let text = value == null ? '' : String(value);
    if (/^[=+\-@]/.test(text.trim())) text = `'${text}`;
    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
}

export function buildTemplateCsv(template: BulkTemplateResult) {
    const universal = template.view === 'universal';
    const header = templateHeaders(template.view);
    const lines = [header.join(',')];
    for (const row of template.rows) {
        const keyword = universal
            ? [row.primaryKeyword, row.secondaryKeyword].filter(Boolean).join(' / ')
            : row.keyword;
        const cells = universal
            ? [
                template.carrierName, template.carrierId, template.lineOfBusiness, template.lineOfBusinessId,
                row.primaryKeyword, row.secondaryKeyword ?? '', row.universalKeyPairId, row.assocId,
                row.stateCode, row.usStateId, row.businessClass ?? '', '',
            ]
            : [
                template.carrierName, template.carrierId, template.lineOfBusiness, template.lineOfBusinessId,
                keyword, row.keywordId, row.assocId, row.stateCode, row.usStateId, row.businessClass ?? '', '',
            ];
        lines.push(cells.map(csvCell).join(','));
    }
    return `\uFEFF${lines.join('\r\n')}`;
}

export function keywordLabel(row: BulkTemplateRow) {
    if (row.keyword) return row.keyword;
    return [row.primaryKeyword, row.secondaryKeyword].filter(Boolean).join(' / ');
}

function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    const source = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    for (let i = 0; i < source.length; i++) {
        const ch = source[i];
        if (quoted) {
            if (ch === '"') {
                if (source[i + 1] === '"') {
                    cell += '"';
                    i++;
                } else {
                    quoted = false;
                }
            } else {
                cell += ch;
            }
            continue;
        }
        if (ch === '"') {
            quoted = true;
            continue;
        }
        if (ch === ',') {
            row.push(cell);
            cell = '';
            continue;
        }
        if (ch === '\n') {
            row.push(cell);
            if (row.some(value => value.trim() !== '')) rows.push(row);
            row = [];
            cell = '';
            continue;
        }
        cell += ch;
    }
    if (cell.length > 0 || row.length > 0) {
        row.push(cell);
        if (row.some(value => value.trim() !== '')) rows.push(row);
    }
    return rows;
}

function parseActive(value: string): number | null {
    const text = value.trim().toLowerCase();
    if (text === '1' || text === 'active' || text === 'on' || text === 'yes') return 1;
    if (text === '0' || text === 'inactive' || text === 'off' || text === 'no') return 0;
    return null;
}

export function parseTemplateUpload(text: string, template: BulkTemplateResult): ParsedBulkUpload {
    const table = parseCsv(text);
    const errors: string[] = [];
    if (table.length === 0) {
        return { rows: [], errors: ['The file is empty.'], warnings: [], activeCount: 0, inactiveCount: 0 };
    }

    const expected = templateHeaders(template.view);
    const header = table[0].map(cell => cell.trim());
    if (header.length !== expected.length || header.some((cell, index) => cell !== expected[index])) {
        return {
            rows: [],
            errors: [`Use the downloaded template. Expected columns: ${expected.join(', ')}.`],
            warnings: [],
            activeCount: 0,
            inactiveCount: 0,
        };
    }

    const assocIndex = template.view === 'universal'
        ? header.indexOf('LobUniversalKeywordAssocId')
        : header.indexOf('LobKeywordAssocId');
    const carrierIndex = header.indexOf('CarrierId');
    const lobIndex = header.indexOf('LineOfBusinessId');
    const stateIdIndex = header.indexOf('UsStateId');
    const stateIndex = header.indexOf('State');
    const classIndex = header.indexOf('BusinessClass');
    const activeIndex = header.indexOf('Active');
    const maxClass = businessClassLimit(template.view);
    const expectedKeys = new Map(template.rows.map(row => [`${row.assocId}:${row.usStateId}`, row]));
    const seen = new Set<string>();
    const classByAssoc = new Map<number, string>();
    const rows: ParsedBulkRow[] = [];

    if (template.rows.length > BULK_ROW_LIMIT) {
        return {
            rows: [],
            errors: [`The template is limited to ${BULK_ROW_LIMIT} rows.`],
            warnings: [],
            activeCount: 0,
            inactiveCount: 0,
        };
    }

    const identityMismatch = table.slice(1).some(cells =>
        Number(cells[carrierIndex]) !== template.carrierId
        || Number(cells[lobIndex]) !== template.lineOfBusinessId);
    if (identityMismatch) {
        return {
            rows: [],
            errors: ['CarrierId and LineOfBusinessId must match the prepared template.'],
            warnings: [],
            activeCount: 0,
            inactiveCount: 0,
        };
    }

    table.slice(1).forEach((cells, index) => {
        const line = index + 2;
        const assocId = Number(cells[assocIndex]);
        const usStateId = Number(cells[stateIdIndex]);
        const key = `${assocId}:${usStateId}`;
        const source = expectedKeys.get(key);
        if (!source) {
            errors.push(`Row ${line} is not in the template for this carrier, line of business, and states.`);
            return;
        }
        if (seen.has(key)) {
            errors.push(`Row ${line} repeats a keyword and state.`);
            return;
        }
        seen.add(key);

        const uploadedState = (cells[stateIndex] ?? '').trim();
        if (uploadedState && uploadedState !== source.stateCode) {
            errors.push(`Row ${line} state must stay ${source.stateCode}.`);
            return;
        }

        const businessClass = (cells[classIndex] ?? '').trim();
        if (!businessClass) {
            errors.push(`Row ${line} needs a business class.`);
            return;
        }
        if (businessClass.length > maxClass) {
            errors.push(`Row ${line} business class must be ${maxClass} characters or fewer.`);
            return;
        }
        const previous = classByAssoc.get(assocId);
        if (previous != null && previous !== businessClass) {
            errors.push(`Row ${line} uses a different business class for the same keyword.`);
            return;
        }
        classByAssoc.set(assocId, businessClass);

        const weight = parseActive(cells[activeIndex] ?? '');
        if (weight == null) {
            errors.push(`Row ${line} Active must be 1 or 0.`);
            return;
        }

        rows.push({
            assocId,
            usStateId,
            stateCode: source.stateCode,
            keyword: keywordLabel(source),
            businessClass,
            weight,
        });
    });

    if (rows.length > BULK_ROW_LIMIT) {
        return {
            rows: [],
            errors: [`The template is limited to ${BULK_ROW_LIMIT} rows.`],
            warnings: [],
            activeCount: 0,
            inactiveCount: 0,
        };
    }

    const warnings: string[] = [];
    const skipped = expectedKeys.size - seen.size;
    if (errors.length === 0 && skipped > 0) {
        warnings.push(
            `The file is missing ${skipped} template rows. Those keyword and state combinations will not be written to business class or appetite.`);
    }

    return {
        rows,
        errors,
        warnings,
        activeCount: rows.filter(row => row.weight === 1).length,
        inactiveCount: rows.filter(row => row.weight === 0).length,
    };
}

export function bulkSetupJson(template: BulkTemplateResult, rows: ParsedBulkRow[]) {
    return JSON.stringify({
        view: template.view,
        carrierId: template.carrierId,
        lineOfBusinessId: template.lineOfBusinessId,
        states: Array.from(new Set(rows.map(row => row.stateCode))),
        rows: rows.map(row => ({
            assocId: row.assocId,
            usStateId: row.usStateId,
            businessClass: row.businessClass,
            weight: row.weight,
            stateCode: row.stateCode,
            keyword: row.keyword,
        })),
    });
}
